import { useState, ReactNode } from 'react';
import { Box, Card, Stack } from '@mui/material';
import { colors, Typography, Tooltip, Button, Tag } from '@sweep-io/sweep-design';
import { Globe, Users } from '@sweep-io/sweep-design/dist/icons';
import { Artifact, ArtifactCardVariant } from '../../../types/artifactsTypes';
import { ArtifactCardIllustration } from './ArtifactCardIllustration';
import { humanizeDate, humanizeDateVariants } from '../../helpers/humanizeDate';
import { stripQuotes } from '../../../lib/stripQuotes';
import { TruncatedTextTooltip } from '../../common/TruncatedTextTooltip';
import { UserInfo } from '../../../types/UserInfoTypes';
import { ShimmerOverlay } from '../../animations/ShimmerOverlay';

export type ArtifactCardStatus = 'complete' | 'generating' | 'failed';

interface ArtifactCardProps {
  artifact?: Artifact;
  variant: ArtifactCardVariant;
  onClick?: () => void;
  usersById: Record<string, UserInfo>;
  /** Custom actions slot - rendered in the top-right corner on hover */
  actionsSlot?: ReactNode;
  status?: ArtifactCardStatus;
  fallbackTitle?: string;
  onRetry?: () => void;
}

interface CardShellProps {
  children: ReactNode;
  actionsSlot?: ReactNode;
  shimmer?: boolean;
  enableHover?: boolean;
  onClick?: () => void;
  backgroundColor?: string;
}

const CardShell = ({
  children,
  actionsSlot,
  shimmer = false,
  enableHover = true,
  onClick,
  backgroundColor = 'white',
}: CardShellProps) => {
  const [isHovered, setIsHovered] = useState(false);

  const card = (
    <Card
      onClick={onClick}
      onMouseEnter={enableHover ? () => setIsHovered(true) : undefined}
      onMouseLeave={enableHover ? () => setIsHovered(false) : undefined}
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        borderRadius: '8px',
        border: `1px solid ${enableHover && isHovered ? colors.blue[500] : colors.grey[300]}`,
        boxShadow: 'none',
        transition: 'border-color 0.2s ease-in-out',
        overflow: 'hidden',
        position: 'relative',
        backgroundColor,
      }}
    >
      {/* Actions slot - show on hover */}
      {actionsSlot && isHovered && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 2,
          }}
        >
          {actionsSlot}
        </Box>
      )}

      {/* Illustration */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
          padding: 2,
          minHeight: '120px',
          pb: 0,
        }}
      >
        <ArtifactCardIllustration />
      </Box>

      <Stack p={2} gap={0.5} sx={{ boxShadow: '0px 2px 12px 0px rgba(0, 0, 0, 0.1)' }}>
        {children}
      </Stack>
    </Card>
  );

  if (shimmer) {
    return (
      <ShimmerOverlay duration={5} isAnimating>
        {card}
      </ShimmerOverlay>
    );
  }

  return card;
};

export const ArtifactCard = ({
  artifact,
  variant,
  onClick,
  usersById,
  actionsSlot,
  status = 'complete',
  fallbackTitle,
  onRetry,
}: ArtifactCardProps) => {
  if (status === 'complete' && !artifact) return null;

  const title = artifact ? stripQuotes(artifact.title) : fallbackTitle || 'Artifact';

  const createdByName = artifact ? usersById[artifact.createdById]?.name || 'Unknown' : '';
  const formattedDate = artifact
    ? humanizeDate({
        dateOrTimestamp: artifact.createdAt,
        variant: humanizeDateVariants.SHORT_WITHOUT_TIME,
      })
    : '';

  const getSubtitle = () => {
    switch (status) {
      case 'generating':
        return 'Generating...';
      case 'failed':
        if (formattedDate) {
          return variant === 'shared'
            ? `Created on ${formattedDate} by ${createdByName}`
            : `Created on ${formattedDate}`;
        }
        return 'Generation failed';
      case 'complete':
      default:
        return variant === 'shared'
          ? `Created on ${formattedDate} by ${createdByName}`
          : `Created on ${formattedDate}`;
    }
  };
  const subtitle = getSubtitle();

  // Actions slot
  const computedActionsSlot = (() => {
    if (status === 'failed' && onRetry) {
      return (
        <Button
          size="tiny"
          variant="flat-grey"
          startIconName="Redo"
          onClick={(e) => {
            e.stopPropagation();
            onRetry();
          }}
        >
          Re-try
        </Button>
      );
    }
    if (status === 'complete') {
      return actionsSlot;
    }
    return undefined;
  })();

  const showShimmer = status === 'generating';
  const enableHover = status !== 'generating';
  const showFailedBadge = status === 'failed';
  const showStatusIcons =
    status === 'complete' &&
    artifact &&
    (artifact.isPublic || (variant === 'my-artifacts' && artifact.isShared));
  const backgroundColor = status === 'failed' ? colors.grey[100] : 'white';

  return (
    <CardShell
      actionsSlot={computedActionsSlot}
      shimmer={showShimmer}
      enableHover={enableHover}
      onClick={status === 'complete' ? onClick : undefined}
      backgroundColor={backgroundColor}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
        <TruncatedTextTooltip title={title}>
          <Typography variant="body-medium" color={colors.grey[900]}>
            {title}
          </Typography>
        </TruncatedTextTooltip>
        {showFailedBadge && (
          <Tag color={colors.blush[100]} label="Generation failed" startIconName="Warning" />
        )}
      </Stack>

      <Stack direction="row" alignItems="center" gap={0.5} sx={{ flexWrap: 'nowrap' }}>
        <Box
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flexShrink: 1,
            minWidth: 0,
          }}
        >
          <Typography variant="caption" color={colors.grey[500]}>
            {subtitle}
          </Typography>
        </Box>

        {showStatusIcons && (
          <Stack direction="row" alignItems="center" gap={0.5} sx={{ flexShrink: 0 }}>
            <Typography variant="caption" color={colors.grey[500]}>
              |
            </Typography>
            {artifact.isPublic && (
              <Tooltip title="Published: Public link enabled" placement="top">
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Globe size="medium" color={colors.grey[900]} />
                </Box>
              </Tooltip>
            )}
            {variant === 'my-artifacts' && artifact.isShared && (
              <Tooltip title="Shared with your company" placement="top">
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Users size="medium" color={colors.grey[900]} />
                </Box>
              </Tooltip>
            )}
          </Stack>
        )}
      </Stack>
    </CardShell>
  );
};
